import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Eye, Pencil, Trash2, Link2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/features/auth/stores/auth.store';
import { usePaymentMutations } from '../hooks';
import { AttachPaymentBillDialog } from './AttachPaymentBillDialog';
import { EditPaymentDialog } from './EditPaymentDialog';
import type { Payment } from '../types';

interface PaymentActionsCellProps {
  payment: Payment;
}

// Payment row actions. A payment is recorded as accepted and is editable and
// deletable in any status (2026-07-17) — there is no approval step, so no
// action here is gated on the payment's status.
export function PaymentActionsCell({ payment }: PaymentActionsCellProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const hasAccess = useAuthStore((state) => state.hasAccess);
  const { deleteMutation } = usePaymentMutations();
  const [attachOpen, setAttachOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  const { id } = payment;

  // An advance/unallocated payment (no bill) can be allocated to one of its
  // contract's UNPAID bills — §7.3 "разнесение".
  const canAttachBill =
    hasAccess('payment', 'update') && !payment.billId && !!payment.contractId;
  const canEdit = hasAccess('payment', 'update');
  const canDelete = hasAccess('payment', 'delete');

  return (
    <div className="flex">
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => navigate(`/app/payments/${id}`)}
        className="rounded-r-none border-r-0 hover:bg-slate-100 dark:hover:bg-slate-800"
      >
        <Eye size={16} className="text-slate-600 dark:text-slate-400" />
      </Button>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="rounded-l-none"
          >
            <ChevronDown size={20} />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-[180px]">
          {/* View */}
          <DropdownMenuItem
            onClick={() => navigate(`/app/payments/${id}`)}
            className="cursor-pointer"
          >
            <Eye className="mr-2 h-4 w-4 text-slate-500" />
            <span>{t('view')}</span>
          </DropdownMenuItem>

          {/* Edit */}
          {canEdit && (
            <DropdownMenuItem
              onClick={() => setEditOpen(true)}
              className="cursor-pointer"
            >
              <Pencil className="mr-2 h-4 w-4 text-slate-500" />
              <span>{t('edit')}</span>
            </DropdownMenuItem>
          )}

          {/* Attach to bill (unallocated/advance payments only) */}
          {canAttachBill && (
            <DropdownMenuItem
              onClick={() => setAttachOpen(true)}
              className={cn(
                'cursor-pointer',
                'text-blue-600 dark:text-blue-500',
                'focus:text-blue-700 focus:bg-blue-50 dark:focus:bg-blue-950/50'
              )}
            >
              <Link2 className="mr-2 h-4 w-4" />
              <span className="font-medium">{t('attachToBill')}</span>
            </DropdownMenuItem>
          )}

          {/* Delete */}
          {canDelete && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => deleteMutation.mutate(id)}
                disabled={deleteMutation.isPending}
                className={cn(
                  'cursor-pointer',
                  'text-red-600 dark:text-red-500',
                  'focus:text-red-700 focus:bg-red-50 dark:focus:bg-red-950/50'
                )}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                <span className="font-medium">{t('delete')}</span>
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {canAttachBill && (
        <AttachPaymentBillDialog
          payment={payment}
          open={attachOpen}
          onOpenChange={setAttachOpen}
        />
      )}

      {canEdit && (
        <EditPaymentDialog
          payment={payment}
          open={editOpen}
          onOpenChange={setEditOpen}
        />
      )}
    </div>
  );
}
